import { getComputedStyle } from '@/utils/utils'
import { type Ref, computed, onBeforeUnmount, ref } from 'vue'

export type GridObserverReturn = {
  /** Stop the grid observer */
  stop: () => void
  /** The computed grid columns */
  computedGridColumns: Ref<number>
  /** The computed grid rows */
  computedGridRows: Ref<number>
  /** The computed remaining unfilled columns */
  computedRemainingColumns: Ref<number>
}

export type GridObserverCallback = (params: {
  /** The grid container element */
  element: Element
  /** The computed grid columns */
  computedGridColumns: Ref<number>
  /** The computed grid rows */
  computedGridRows: Ref<number>
  /** The computed remaining unfilled columns */
  computedRemainingColumns: Ref<number>
}) => void

/**
 * Observe changes to a grid container's size and layout.
 *
 * Note: The container element must have the 'grid' class for proper calculations.
 * Note 2: Calculations are first attempted using CSS grid properties. If these are not defined, it falls back to calculating based on the size of the container and its child elements.
 *
 * @param container The grid container element or a selector string.
 * @param callback The function to call when the grid layout changes. It receives the container element and computed grid rows and columns as parameters.
 * @param excludeElements Optional class name or HTMLElement to exclude certain elements from the grid calculations. Default is 'coming-soon-card'.
 * @returns An object with a stop method to disconnect the observer, and refs for computed grid columns, rows, and remaining unfilled columns.
 */
export function useGridObserver(
  container: string | HTMLElement,
  callback: GridObserverCallback,
  excludeElements: string | HTMLElement[] = 'coming-soon-card'
): GridObserverReturn | undefined {
  const containerElement =
    typeof container === 'string'
      ? document.querySelector(container)
      : container

  if (!containerElement) {
    console.warn(`No elements found for container:`, container)
    return
  }

  const computedGridColumns = ref(0)
  const computedGridRows = ref(0)
  const computedRemainingColumns = ref(0)
  const containerResizeObserver = new ResizeObserver(() => {
    calculateGridRowsAndColumns()
    callback({
      element: containerElement,
      computedGridColumns,
      computedGridRows,
      computedRemainingColumns,
    })
  })

  const isUsingGridColumns = computed(() => {
    const gridColumns = getComputedStyle(
      containerElement,
      'grid-template-columns'
    )

    return !!gridColumns && gridColumns !== 'none'
  })

  const isUsingGridRows = computed(() => {
    const gridRows = getComputedStyle(containerElement, 'grid-template-rows')

    return !!gridRows && gridRows !== 'none'
  })

  containerResizeObserver.observe(containerElement)

  function calculateGridRowsAndColumns() {
    if (!containerElement) {
      console.warn(`No elements found for container:`, container)
      return
    }

    // Get the computed column count of the container
    calculateGridCols(containerElement)

    // Get the computed row count of the container
    calculateGridRows(containerElement)

    // Calculate remaining unfilled columns
    calculateRemainingUnfilledColumns(containerElement)
  }

  function calculateRemainingUnfilledColumns(containerElement: Element) {
    const elementCount =
      typeof excludeElements === 'string'
        ? Array.from(
            containerElement.querySelectorAll(
              `:scope > *:not([class*="${excludeElements}"])`
            )
          ).length
        : excludeElements.length

    const remainingColumns =
      !isUsingGridColumns.value ||
      elementCount % computedGridColumns.value === 0
        ? 0
        : computedGridColumns.value - (elementCount % computedGridColumns.value)

    if (remainingColumns < 1) {
      return
    }

    computedRemainingColumns.value = remainingColumns
  }

  function calculateGridCols(containerElement: Element) {
    const gridColumns = getComputedStyle(
      containerElement,
      'grid-template-columns'
    )

    if (gridColumns && isUsingGridColumns.value) {
      const gridColumnsInt = parseInt(gridColumns.match(/(\d+)/)?.[1] ?? '0')
      computedGridColumns.value = gridColumnsInt
    } else {
      // Get the total width of the grid
      const gridWidth = containerElement.getBoundingClientRect().width

      // Get the width of the first grid item (assuming all items have same width)
      const firstGridItem = containerElement.querySelector(':scope > *')
      const itemWidth = firstGridItem
        ? firstGridItem.getBoundingClientRect().width
        : 0

      // Calculate number of columns (round to nearest integer to handle any minor pixel differences)
      const colCount = itemWidth ? Math.floor(gridWidth / itemWidth) : 0
      computedGridColumns.value = colCount
    }
  }

  function calculateGridRows(containerElement: Element) {
    const gridRows = getComputedStyle(containerElement, 'grid-template-rows')

    if (gridRows && isUsingGridRows.value) {
      const gridRowsInt = parseInt(gridRows.match(/(\d+)/)?.[1] ?? '0')
      computedGridRows.value = gridRowsInt
    } else {
      // Get the total height of the grid
      const gridHeight = containerElement.getBoundingClientRect().height

      // Get the height of the first grid item (assuming all items have same height)
      const firstGridItem = containerElement.querySelector(':scope > *')
      const itemHeight = firstGridItem
        ? firstGridItem.getBoundingClientRect().height
        : 0

      // Calculate number of rows (round to nearest integer to handle any minor pixel differences)
      const rowCount = itemHeight ? Math.floor(gridHeight / itemHeight) : 0
      computedGridRows.value = rowCount
    }
  }

  onBeforeUnmount(() => {
    containerResizeObserver.disconnect()
  })

  return {
    stop: () => containerResizeObserver.disconnect(),
    computedGridColumns,
    computedGridRows,
    computedRemainingColumns,
  }
}
